import { Helmet } from 'react-helmet-async';
import { Box, Button, Container, Stack, TextField, Typography, MenuItem, Select, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Iconify from '../components/iconify';
import { RiDeleteBinLine } from 'react-icons/ri';
import { BiEdit } from 'react-icons/bi';
import useApi from '../hooks/useApi';
import { useAppContext } from '../contexts/AppContext';
import { getUserBadges, createUserBadge, updateUserBadge, deleteUserBadge } from '../services/Common/userBadge.service';
import { getUserList } from '../services/Common/user.service';
import { getCategoryList } from '../services/Common/category.service';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import palette from '../theme/palette';

const RANKS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Master', 'Grandmaster', 'Elite'];
const ROLES = ['IGL', 'Sniper', 'Rusher', 'Support', 'All-Rounder'];

export default function UserBadgePage() {
    const { setLoading } = useAppContext();
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBadge, setEditingBadge] = useState(null);

    const [formUser, setFormUser] = useState('');
    const [formCategory, setFormCategory] = useState('');
    const [formRank, setFormRank] = useState('');
    const [formLevel, setFormLevel] = useState('');
    const [formRole, setFormRole] = useState('');

    const fetchUsers = async () => {
        try {
            const res = await getUserList();
            setUsers(res || []);
        } catch (e) { }
    };

    const fetchCategories = async () => {
        try {
            const res = await getCategoryList();
            setCategories(res || []);
        } catch (e) { }
    };

    useEffect(() => {
        fetchUsers();
        fetchCategories();
    }, []);

    const { result, setResult } = useApi(() => getUserBadges(), { onLoading: (status) => setLoading(status) });

    const resetForm = () => {
        setFormUser('');
        setFormCategory('');
        setFormRank('');
        setFormLevel('');
        setFormRole('');
        setEditingBadge(null);
    };

    const openCreateDialog = () => {
        resetForm();
        setDialogOpen(true);
    };

    const openEditDialog = (badge) => {
        setEditingBadge(badge);
        setFormUser(badge.user?._id || badge.user || '');
        setFormCategory(badge.category?._id || badge.category || '');
        setFormRank(badge.rank || '');
        setFormLevel(badge.level || '');
        setFormRole(badge.role || '');
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formUser || !formCategory) {
            toast.error('User and Game are required');
            return;
        }
        setLoading(true);
        try {
            const payload = { user: formUser, category: formCategory, rank: formRank, level: formLevel, role: formRole };
            if (editingBadge) {
                await updateUserBadge(editingBadge._id, payload);
                toast.success('Badge updated');
            } else {
                await createUserBadge(payload);
                toast.success('Badge created');
            }
            const updated = await getUserBadges();
            setResult(updated);
            setDialogOpen(false);
            resetForm();
        } catch (e) {
            toast.error(e?.response?.data?.data?.message || 'Operation failed');
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this badge?')) return;
        setLoading(true);
        try {
            await deleteUserBadge(id);
            toast.success('Badge deleted');
            const updated = await getUserBadges();
            setResult(updated);
        } catch (e) {
            toast.error('Failed to delete');
        }
        setLoading(false);
    };

    function getRowId(row) { return row._id; }

    const columns = [
        {
            field: 'user', headerName: 'User', minWidth: 180, flex: 1, align: 'center', headerAlign: 'center',
            renderCell: (params) => <span>{params.row.user?.name || params.row.user || '-'}</span>,
        },
        {
            field: 'category', headerName: 'Game', minWidth: 150, flex: 1, align: 'center', headerAlign: 'center',
            renderCell: (params) => <span>{params.row.category?.game_title || params.row.category || '-'}</span>,
        },
        {
            field: 'rank', headerName: 'Rank', minWidth: 120, flex: 1, align: 'center', headerAlign: 'center',
            renderCell: (params) => <span>{params.value || '-'}</span>,
        },
        {
            field: 'level', headerName: 'Level', minWidth: 100, flex: 1, align: 'center', headerAlign: 'center',
            renderCell: (params) => <span>{params.value || '-'}</span>,
        },
        {
            field: 'role', headerName: 'Role', minWidth: 120, flex: 1, align: 'center', headerAlign: 'center',
            renderCell: (params) => <span>{params.value || '-'}</span>,
        },
        {
            headerName: 'Action', field: 'actions', minWidth: 160, flex: 1, align: 'center', headerAlign: 'center',
            renderCell: (params) => (
                <>
                    <Button onClick={() => openEditDialog(params.row)}><BiEdit color='black' size={20} /></Button>
                    <Button onClick={() => handleDelete(params.row._id)}><RiDeleteBinLine color='red' size={20} /></Button>
                </>
            ),
        },
    ];

    return (
        <>
            <Helmet><title>User Badges | Trooper</title></Helmet>
            <Container>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4">User Badges</Typography>
                    <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openCreateDialog}>
                        Add Badge
                    </Button>
                </Stack>

                <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
                    <DialogTitle>{editingBadge ? 'Edit Badge' : 'Add Badge'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <FormControl fullWidth>
                                <InputLabel>User</InputLabel>
                                <Select value={formUser} label="User" onChange={(e) => setFormUser(e.target.value)}>
                                    {users.map((u) => (
                                        <MenuItem key={u._id} value={u._id}>{u.name} ({u.email})</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Game</InputLabel>
                                <Select value={formCategory} label="Game" onChange={(e) => setFormCategory(e.target.value)}>
                                    {categories.map((c) => (
                                        <MenuItem key={c._id} value={c._id}>{c.game_title}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Rank</InputLabel>
                                <Select value={formRank} label="Rank" onChange={(e) => setFormRank(e.target.value)}>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {RANKS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField label="Level" value={formLevel} onChange={(e) => setFormLevel(e.target.value)} placeholder="e.g. 50, Pro, Elite" fullWidth />
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select value={formRole} label="Role" onChange={(e) => setFormRole(e.target.value)}>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit}>{editingBadge ? 'Update' : 'Create'}</Button>
                    </DialogActions>
                </Dialog>

                <div style={{ height: '70vh', minWidth: '100%' }}>
                    {result && result.length > 0 ? (
                        <DataGrid rows={result} rowCount={result.length} columns={columns} getRowId={getRowId} pageSizeOptions={[5, 10, 50]} />
                    ) : (
                        <Box sx={{ textAlign: 'center', color: palette.grey[600], fontSize: '18px', mt: 10 }}>No badges assigned yet</Box>
                    )}
                </div>
            </Container>
        </>
    );
}
